import {
  Dialog,
  DialogContent,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ArrowRight, Crown } from 'lucide-react';
import type { Plan } from '@deepguide-ai/types';
import { useEffect, useRef } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useAtomValue } from 'jotai';
import { workspaceIdAtom } from '@/stores/workspaceStore';
import {
  trackUpgradePromptShown,
  trackUpgradePromptDismissed,
} from '@/utils/analytics/planRestrictionEvents';

const PRICING_URL = import.meta.env.VITE_PRICING_URL || 'https://glydehq.com/#pricing';

export interface UpgradeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUpgradeClick?: () => void;

  // Feature context
  featureName?: string;
  featureDescription?: string;
  /** @deprecated No longer displayed in the simplified dialog */
  featureIcon?: React.ReactNode;
  /** @deprecated No longer displayed in the simplified dialog */
  featureBenefits?: string[];
  /** @deprecated No longer displayed in the simplified dialog */
  customMessage?: string;
  /** @deprecated No longer displayed in the simplified dialog */
  showComparison?: boolean;

  // Plan context
  currentPlan: Plan;
  requiredPlan: Plan;

  // Analytics
  source?: string; // Track where upgrade was triggered
}

/**
 * Simplified upgrade dialog with centered icon + CTA
 *
 * @example
 * ```tsx
 * <UpgradeDialog
 *   open={isOpen}
 *   onOpenChange={setIsOpen}
 *   featureName="Export to HTML"
 *   currentPlan="FREE"
 *   requiredPlan="PRO"
 *   source="export_page"
 * />
 * ```
 */
export function UpgradeDialog({
  open,
  onOpenChange,
  onUpgradeClick,
  featureName,
  featureDescription,
  currentPlan,
  requiredPlan,
  source,
}: UpgradeDialogProps) {
  const navigate = useNavigate();
  const workspaceId = useAtomValue(workspaceIdAtom);
  const shownTimeRef = useRef<number | null>(null);
  const hasTrackedShown = useRef(false);

  // Track when dialog is shown
  useEffect(() => {
    if (open && !hasTrackedShown.current) {
      shownTimeRef.current = Date.now();
      trackUpgradePromptShown({
        feature: featureName || 'unknown',
        source: source || 'unknown',
        currentPlan,
        requiredPlan,
      });
      hasTrackedShown.current = true;
    }

    // Reset tracking flag when dialog closes
    if (!open) {
      hasTrackedShown.current = false;
    }
  }, [open, featureName, source, currentPlan, requiredPlan]);

  const handleUpgrade = () => {
    onUpgradeClick?.();
    onOpenChange(false);
    navigate({
      to: '/settings/billing',
    });
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      // Track dismissal with time on screen
      const timeOnScreen = shownTimeRef.current
        ? Date.now() - shownTimeRef.current
        : undefined;

      trackUpgradePromptDismissed({
        feature: featureName || 'unknown',
        source: source || 'unknown',
        requiredPlan,
        currentPlan,
        timeOnScreen,
      });
    }
    onOpenChange(nextOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        <div className="flex flex-col items-center text-center gap-5 py-2">
          {/* Crown icon */}
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-sidebar-primary/10">
            <Crown className="h-7 w-7 text-sidebar-primary" />
          </div>

          {/* Title + description */}
          <div className="space-y-2">
            <h2 className="text-[22px] font-semibold tracking-tight">
              Upgrade to Pro
            </h2>
            {featureDescription ? (
              <p className="text-sm leading-relaxed text-muted-foreground">
                {featureDescription}
              </p>
            ) : featureName ? (
              <p className="text-sm leading-relaxed text-muted-foreground">
                Unlock {featureName} and other advanced features with a Pro plan.
              </p>
            ) : (
              <p className="text-sm leading-relaxed text-muted-foreground">
                Unlock advanced features with a Pro plan.
              </p>
            )}
          </div>

          {/* Upgrade button */}
          <Button
            onClick={handleUpgrade}
            className="w-full gap-2 h-11 rounded-md bg-sidebar-primary hover:bg-sidebar-primary/90"
          >
            Upgrade to Pro
            <ArrowRight className="h-4 w-4" />
          </Button>

          {/* Learn more link */}
          <a
            href={PRICING_URL}
            className="text-xs text-muted-foreground hover:underline"
            onClick={(e) => {
              e.preventDefault();
              window.open(PRICING_URL, '_blank');
            }}
          >
            Learn more about plans
          </a>
        </div>
      </DialogContent>
    </Dialog>
  );
}
